import { Api, Filters } from '@firmable/contracts';
import { ObjectHelper } from '@firmable/utilities';
import { useQuery } from '@tanstack/react-query';
import { Query } from '@web/constants/query';
import { Search } from '@web/store/Search';
import { useFilters } from './useFilters';

export interface Failure {
  message: string;
}

type GroupedFilters = {
  name: Filters.Groups;
  items: Api.DynamoDB.FilterItem[];
  order: number;
};

export type FiltersApiResponse = GroupedFilters[];

type QueryParams = {
  enabled: boolean;
};

/**
 * React Query hook to filters available by group
 * @returns FiltersApiResponse | undefined
 */
const useFiltersByGroup = ({ enabled }: QueryParams) => {
  const source = Search.useFilter.getState().source;
  const isCoreSearch = source === 'core-filters';
  const { data: availableFilters } = useFilters(isCoreSearch);

  return useQuery<FiltersApiResponse, Failure>({
    queryKey: [Query.FilterKeys['groups']],
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: enabled,
    retry: false,
    queryFn: async () => {
      // Is this really need on the here or dumping into React query make sense? , will decide later
      if (!availableFilters) return [];

      let result: GroupedFilters[] = [];

      const filters = availableFilters?.filters;

      const groupedData = Object.values(filters).reduce(
        (curr, item) => {
          if (!curr[item.group]) {
            curr[item.group] = [];
          }
          curr[item.group].push(item);
          return curr;
        },
        {} as Record<Filters.Groups, Api.DynamoDB.FilterItem[]>,
      );

      // sort each group by the "order" field
      ObjectHelper.getKeys(groupedData).forEach((group) => groupedData[group].sort((a, b) => a.order - b.order));

      // convert the grouped data to an array of groups
      ObjectHelper.getKeys(groupedData).forEach((group) => {
        result.push({
          order: availableFilters.groups[group].order,
          name: group,
          items: groupedData[group],
        });
      });

      // sort the groups by the "order" field
      result.sort((a, b) => a.order - b.order);

      return result;
    },
  });
};

export { useFiltersByGroup };
